import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft, CalendarRange, AlertTriangle, Copy, ChevronDown, Search, ClipboardCopy, X, Music,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};
const SLOT_LABELS = {
  morning: { label: 'Morning', sub: '6 AM – 12 PM' },
  afternoon: { label: 'Afternoon', sub: '12 PM – 5 PM' },
  evening: { label: 'Evening', sub: '5 PM – 9 PM' },
  night: { label: 'Night', sub: '9 PM – 6 AM' },
};

const AdminClientSchedulePage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playlists, setPlaylists] = useState([]);
  const [allClients, setAllClients] = useState([]);
  // Assign modal state
  const [assignCell, setAssignCell] = useState(null);   // { day, slot, current_playlist_id|null } | null
  const [assignSearch, setAssignSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // Copy modal state
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState('');
  const [copyOverwrite, setCopyOverwrite] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/clients/${clientId}/schedule`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load schedule', { description: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    // Lazy-load the playlist + clients list when the user first triggers an action
    if (assignCell || copyOpen) {
      if (playlists.length === 0) {
        axios.get(`${API}/admin/playlists`).then((r) => setPlaylists(r.data || [])).catch(() => {});
      }
      if (allClients.length === 0 && copyOpen) {
        axios.get(`${API}/admin/clients`).then((r) => setAllClients(r.data || [])).catch(() => {});
      }
    }
  }, [assignCell, copyOpen, playlists.length, allClients.length]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-gray-500">Loading schedule…</div>
      </AdminLayout>
    );
  }
  if (!data) {
    return (
      <AdminLayout>
        <div className="p-8 text-gray-500">Schedule not available.</div>
      </AdminLayout>
    );
  }

  const { client, schedule, days, slots, empty_slots, duplicates, stats } = data;
  // Build a set of "duplicate" cells for quick lookup
  const dupCells = new Set();
  Object.entries(duplicates).forEach(([_pid, list]) => {
    list.forEach(({ day, slot }) => dupCells.add(`${day}:${slot}`));
  });

  const setSlot = async (day, slot, playlist_id) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/clients/${clientId}/schedule/slot`, { day, slot, playlist_id });
      await refresh();
      setAssignCell(null);
      toast.success(playlist_id ? 'Slot assigned' : 'Slot cleared');
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const runCopy = async () => {
    if (!copySource) { toast.error('Pick a source venue'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/clients/${clientId}/schedule/copy-from`, {
        source_id: copySource,
        overwrite: copyOverwrite,
      });
      toast.success(`Copied ${r.data.copied} slot${r.data.copied === 1 ? '' : 's'}`, {
        description: r.data.skipped ? `Skipped ${r.data.skipped} (already filled)` : undefined,
      });
      await refresh();
      setCopyOpen(false);
      setCopySource('');
    } catch (e) {
      toast.error('Copy failed', { description: e?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const filteredPlaylists = playlists.filter((pl) =>
    pl.name?.toLowerCase().includes(assignSearch.toLowerCase()) ||
    pl.description?.toLowerCase().includes(assignSearch.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto pb-32" data-testid="admin-client-schedule-page">
        {/* Back + Heading */}
        <button
          onClick={() => navigate('/admin/clients')}
          className="text-gray-400 hover:text-white text-sm flex items-center gap-1.5 mb-6"
          data-testid="schedule-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Venues
        </button>

        <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-[0.2em] mb-1">Weekly Schedule</p>
            <h1
              className="text-3xl lg:text-4xl font-extrabold text-white"
              style={{ fontFamily: 'Outfit' }}
              data-testid="schedule-client-name"
            >
              {client.name}
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {client.email}{client.venue_code ? ` · ${client.venue_code}` : ''}
            </p>
          </div>

          {/* Stat pills */}
          <div className="flex flex-wrap gap-3 items-center" data-testid="schedule-stats">
            <Button
              onClick={() => setCopyOpen(true)}
              className="bg-[#00FFFF] hover:bg-[#00cccc] text-black font-bold rounded-full"
              data-testid="schedule-copy-from-btn"
            >
              <ClipboardCopy className="w-4 h-4 mr-2" /> COPY FROM…
            </Button>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Fill rate</p>
              <p className="text-2xl font-extrabold text-[#39FF14]">{stats.fill_pct}%</p>
              <p className="text-[10px] text-gray-500">{stats.filled_slots} / {stats.total_slots} slots</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Empty</p>
              <p className={`text-2xl font-extrabold ${stats.empty_slots_count > 0 ? 'text-[#FF3B3B]' : 'text-gray-400'}`}>
                {stats.empty_slots_count}
              </p>
              <p className="text-[10px] text-gray-500">unfilled</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Duplicates</p>
              <p className={`text-2xl font-extrabold ${stats.duplicate_count > 0 ? 'text-[#FFD700]' : 'text-gray-400'}`}>
                {stats.duplicate_count}
              </p>
              <p className="text-[10px] text-gray-500">playlists in 2+ slots</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Assigned</p>
              <p className="text-2xl font-extrabold text-[#00FFFF]">{stats.playlists_assigned}</p>
              <p className="text-[10px] text-gray-500">unique playlists</p>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-3 mb-4 text-[11px] text-gray-400 flex-wrap items-center">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-[#39FF14]/20 border border-[#39FF14]/40 inline-block" /> Filled
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-[#FFD700]/15 border border-[#FFD700]/40 inline-block" /> Duplicate (same playlist as another slot)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-[#FF3B3B]/10 border border-[#FF3B3B]/30 inline-block border-dashed" /> Empty
          </span>
        </div>

        {/* Schedule grid */}
        <Card className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-x-auto" data-testid="schedule-grid">
          <div className="min-w-[860px]">
            {/* Header row */}
            <div className="grid grid-cols-[120px_repeat(7,minmax(0,1fr))] border-b border-white/10">
              <div className="p-3 text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                <CalendarRange className="w-3.5 h-3.5" /> Slot
              </div>
              {days.map((d) => (
                <div key={d} className="p-3 text-center text-xs text-gray-300 uppercase tracking-wider font-semibold">
                  {DAY_LABELS[d] || d}
                </div>
              ))}
            </div>

            {/* Slot rows */}
            {slots.map((slot) => (
              <div
                key={slot}
                className="grid grid-cols-[120px_repeat(7,minmax(0,1fr))] border-b border-white/5 last:border-b-0"
              >
                <div className="p-3 border-r border-white/5 bg-black/30">
                  <p className="text-white text-sm font-semibold">{SLOT_LABELS[slot]?.label || slot}</p>
                  <p className="text-[10px] text-gray-500">{SLOT_LABELS[slot]?.sub || ''}</p>
                </div>
                {days.map((day) => {
                  const cell = schedule[day][slot];
                  const isEmpty = !cell;
                  const isDup = cell && dupCells.has(`${day}:${slot}`);
                  return (
                    <button
                      key={`${day}-${slot}`}
                      type="button"
                      onClick={() => setAssignCell({ day, slot, current_playlist_id: cell?.id || null, current_name: cell?.name || null })}
                      className={`p-2 min-h-[88px] border-r border-white/5 last:border-r-0 flex flex-col text-xs text-left transition-colors hover:bg-white/[0.06] ${
                        isEmpty
                          ? 'bg-[#FF3B3B]/5 border-dashed'
                          : isDup
                          ? 'bg-[#FFD700]/8'
                          : 'bg-[#39FF14]/5'
                      }`}
                      data-testid={`schedule-cell-${day}-${slot}`}
                      title={isEmpty ? 'Click to assign a playlist' : `${cell.name} — click to change`}
                    >
                      {isEmpty ? (
                        <span className="text-[#FF3B3B]/70 m-auto text-[10px] uppercase tracking-wider">Empty</span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {cell.cover_image && (
                            <img
                              src={cell.cover_image}
                              alt=""
                              className="w-full h-10 rounded object-cover border border-white/10"
                            />
                          )}
                          <p className="text-white font-semibold line-clamp-2 leading-tight">{cell.name}</p>
                          <p className="text-gray-500 text-[10px] flex items-center gap-1">
                            {cell.track_count} tracks
                            {isDup && (
                              <span className="text-[#FFD700] flex items-center gap-0.5 ml-auto">
                                <Copy className="w-3 h-3" /> dup
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>

        {/* Issues panel */}
        {(stats.empty_slots_count > 0 || stats.duplicate_count > 0) && (
          <details className="mt-6 bg-white/[0.03] border border-white/10 rounded-2xl p-5" data-testid="schedule-issues">
            <summary className="cursor-pointer flex items-center gap-2 text-white font-semibold list-none">
              <AlertTriangle className="w-4 h-4 text-[#FF3B3B]" />
              {stats.empty_slots_count + stats.duplicate_count} issue{stats.empty_slots_count + stats.duplicate_count === 1 ? '' : 's'} found
              <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
            </summary>
            <div className="mt-4 space-y-4">
              {stats.empty_slots_count > 0 && (
                <div>
                  <p className="text-[#FF3B3B] text-sm font-semibold mb-2">
                    Unfilled slots ({stats.empty_slots_count})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {empty_slots.map((es) => (
                      <span
                        key={`${es.day}-${es.slot}`}
                        className="text-[11px] bg-[#FF3B3B]/10 border border-[#FF3B3B]/30 text-[#FF3B3B] px-2.5 py-1 rounded-full"
                        data-testid={`empty-${es.day}-${es.slot}`}
                      >
                        {DAY_LABELS[es.day]} · {SLOT_LABELS[es.slot]?.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {stats.duplicate_count > 0 && (
                <div>
                  <p className="text-[#FFD700] text-sm font-semibold mb-2">
                    Playlists assigned to multiple slots ({stats.duplicate_count})
                  </p>
                  <div className="space-y-2">
                    {Object.entries(duplicates).map(([pid, list]) => {
                      const sample = (() => {
                        for (const d of days) for (const s of slots) {
                          const c = schedule[d][s];
                          if (c && c.id === pid) return c;
                        }
                        return null;
                      })();
                      return (
                        <div key={pid} className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap" data-testid={`duplicate-${pid}`}>
                          <span className="text-white font-semibold text-sm flex-shrink-0">{sample?.name || pid}</span>
                          <span className="text-gray-300 text-xs">→</span>
                          <div className="flex flex-wrap gap-1.5">
                            {list.map((p) => (
                              <span key={`${p.day}-${p.slot}`} className="text-[11px] bg-black/30 text-[#FFD700] px-2 py-0.5 rounded">
                                {DAY_LABELS[p.day]} · {SLOT_LABELS[p.slot]?.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </details>
        )}

        <div className="mt-6 flex gap-2 flex-wrap">
          <Button asChild className="bg-[#FF007F] hover:bg-[#cc0066] text-white rounded-full">
            <Link to="/admin/playlists">Manage Playlist Assignments</Link>
          </Button>
        </div>
      </div>

      {/* Assign-playlist modal */}
      <Dialog open={!!assignCell} onOpenChange={(o) => !o && setAssignCell(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="assign-slot-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              {assignCell?.current_playlist_id ? 'Change playlist' : 'Assign playlist'}
            </DialogTitle>
          </DialogHeader>
          {assignCell && (
            <>
              <p className="text-gray-400 text-sm mb-3">
                <span className="text-white font-semibold">{DAY_LABELS[assignCell.day]}</span>
                {' · '}
                <span className="text-white font-semibold">{SLOT_LABELS[assignCell.slot]?.label}</span>
                {assignCell.current_name && (
                  <span className="ml-2 text-[#FFD700]">(currently: {assignCell.current_name})</span>
                )}
              </p>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                  placeholder="Search playlists…"
                  className="pl-9 bg-black/40 border-white/10 text-white"
                  data-testid="assign-slot-search"
                />
              </div>
              <div className="max-h-72 overflow-y-auto border border-white/5 rounded-lg divide-y divide-white/5">
                {filteredPlaylists.length === 0 ? (
                  <p className="text-gray-500 text-sm p-4 text-center">No playlists match.</p>
                ) : (
                  filteredPlaylists.map((pl) => {
                    const isCurrent = pl.id === assignCell.current_playlist_id;
                    return (
                      <button
                        key={pl.id}
                        type="button"
                        disabled={saving}
                        onClick={() => setSlot(assignCell.day, assignCell.slot, pl.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-white/[0.05] text-left transition-colors ${isCurrent ? 'bg-[#FF007F]/15' : ''}`}
                        data-testid={`assign-slot-pl-${pl.id}`}
                      >
                        {pl.cover_image ? (
                          <img src={pl.cover_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                            <Music className="w-4 h-4 text-gray-500" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-semibold truncate">{pl.name}</p>
                          <p className="text-gray-500 text-[11px] truncate">{pl.tracks?.length || 0} tracks</p>
                        </div>
                        {isCurrent && <span className="text-[10px] text-[#FF007F] font-bold uppercase tracking-wider">Current</span>}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="flex gap-2 mt-4">
                {assignCell.current_playlist_id && (
                  <Button
                    onClick={() => setSlot(assignCell.day, assignCell.slot, null)}
                    disabled={saving}
                    variant="ghost"
                    className="border border-[#FF3B3B]/30 text-[#FF3B3B] hover:bg-[#FF3B3B]/10 rounded-full"
                    data-testid="assign-slot-clear"
                  >
                    <X className="w-4 h-4 mr-2" /> CLEAR SLOT
                  </Button>
                )}
                <Button onClick={() => setAssignCell(null)} variant="ghost" className="text-gray-400 ml-auto">
                  CANCEL
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Copy-from-template modal */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-md" data-testid="copy-from-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              Copy schedule from another venue
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-sm mb-4">
            Pick a venue whose schedule you want to mirror. This copies every filled day+slot
            from that venue onto <span className="text-white font-semibold">{client.name}</span>.
          </p>
          <label className="text-gray-300 text-xs uppercase tracking-wider">Source venue</label>
          <select
            value={copySource}
            onChange={(e) => setCopySource(e.target.value)}
            className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm mt-1 mb-4"
            data-testid="copy-from-source"
          >
            <option value="">— Select a venue —</option>
            {allClients
              .filter((c) => c.id !== clientId)
              .map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
              ))}
          </select>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-black/30 border border-white/5 mb-4">
            <Switch
              checked={copyOverwrite}
              onCheckedChange={setCopyOverwrite}
              data-testid="copy-from-overwrite"
            />
            <div className="text-xs">
              <p className="text-white font-semibold">Overwrite existing slots</p>
              <p className="text-gray-500">
                {copyOverwrite
                  ? 'Replaces this venue\u2019s current assignments where the source has one.'
                  : 'Only fills slots that are currently empty (safe default).'}
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setCopyOpen(false)} className="text-gray-400">CANCEL</Button>
            <Button
              onClick={runCopy}
              disabled={saving || !copySource}
              className="bg-[#00FFFF] hover:bg-[#00cccc] text-black font-bold rounded-full px-6"
              data-testid="copy-from-submit"
            >
              {saving ? 'COPYING…' : 'COPY SCHEDULE'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminClientSchedulePage;
